import React, { useState, useEffect, useRef } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import './Workspace2.css';
import FeedbackButton from './FeedbackButton';
import CategorySelector from './CategorySelector';
import api from './api';
import { useNavigate } from 'react-router-dom';
import { decodeToken, isTokenExpired } from './utils/jwt';
import { trackEvent } from './utils/amplitude';
import { AlertCircle, Download, ExternalLink, Plus, Zap, X, Tag, Edit2, Video, Mic, Square, Check, Trash2, ChevronUp, ChevronDown, RefreshCw, Play, Pause, Loader } from 'react-feather';
import './App.css';
import './MetaModal.css';
import { useWorkspace } from './WorkspaceContext';
import { useDarkMode } from './DarkModeContext';
import VideoGenerationCard from './VideoGenerationCard';
import axios from 'axios';
import categories from './CategoryData';
import { useVideoGeneration } from './VideoGenerationContext';
import VideoManagement from './VideoManagement';

// Recording Examples
const RECORDING_EXAMPLES = [
    // Introduction & Context
    "Hi, I noticed your impressive work at [Company] and wanted to reach out.",
    "I've been following [Company]'s growth in the [industry] space.",
    "Your recent post about [topic] really resonated with me.",
    "Your background in [field] caught my attention.",
    "I saw your team is expanding the [department/initiative] and thought I'd reach out.",

    // Value Proposition
    "We help companies like yours improve [metric] by [percentage].",
    "Our solution has helped similar businesses save [X] hours per week.",
    "We've helped other [job title]s increase their team's productivity by [percentage].",
    "Our platform typically reduces [pain point] by [metric].",
    "Companies similar to yours have seen [specific result] within [timeframe].",

    // Industry-Specific
    "The [industry] landscape is rapidly changing, especially regarding [trend].",
    "Many [job title]s we work with struggle with [common challenge].",
    "In today's [industry] environment, [pain point] is more crucial than ever.",
    "With recent changes in [regulation/technology], many companies are looking to [goal].",
    "We've noticed a growing trend in [industry] where [observation].",

    // Problem Recognition
    "Many teams face challenges with [common pain point].",
    "I understand how challenging [specific task] can be.",
    "One common issue we see is [problem description].",
    "Teams often struggle to balance [competing priority A] with [priority B].",
    "The complexity of [process] often leads to [negative outcome].",

    // Solution Overview
    "Our approach simplifies [complex process] through [method].",
    "We've developed a unique solution for [specific challenge].",
    "Our platform automates [manual process] while maintaining [important aspect].",
    "We combine [technology A] with [technology B] to achieve [outcome].",
    "Our solution integrates seamlessly with [common tools] to improve [process].",

    // Results & Benefits
    "Clients typically see ROI within [timeframe].",
    "Our average customer saves [amount] per month.",
    "Teams report [percentage] improvement in [metric] after implementation.",
    "We've helped companies reduce [cost/time] by up to [percentage].",
    "Users experience [benefit] while maintaining [important factor].",

    // Social Proof
    "Companies like [known brand] have achieved [result] using our solution.",
    "We recently helped a [industry] company overcome [challenge].",
    "One of our clients in [industry] reported [specific outcome].",
    "A similar [company type] saw [improvement] within [timeframe].",
    "We've worked with several teams in [industry] to improve [metric].",

    // Use Cases
    "This works particularly well for teams that need to [specific use case].",
    "It's especially useful when you're trying to [achieve goal].",
    "Many teams use this to streamline their [process].",
    "This solution is perfect for organizations looking to [objective].",
    "We've seen great results with teams focusing on [specific goal].",

    // Implementation & Support
    "The implementation process typically takes [timeframe].",
    "Our support team provides [type of support] throughout the process.",
    "We offer comprehensive training for [feature/process].",
    "The onboarding process is designed to minimize [common concern].",
    "Our team handles all aspects of [technical process].",

    // Future-Focused
    "We're constantly developing new features for [emerging need].",
    "Our roadmap includes exciting developments in [area].",
    "We're preparing for [industry trend] with [feature/capability].",
    "Our solution adapts to [changing circumstance] automatically.",
    "We're investing heavily in [future technology/capability]."
];

const Workspace = () => {
    const { darkMode } = useDarkMode();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('raw'); // 'raw' or 'generated'
    const [videos, setVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [editingVideo, setEditingVideo] = useState(null);
    const [newTag, setNewTag] = useState('');
    const [newDescription, setNewDescription] = useState('');

    // User info
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState(null);

    // Profile info
    const [profileComplete, setProfileComplete] = useState(false);
    const [userProfile, setUserProfile] = useState({
        currentSituation: '',
        shortTermGoals: '',
        longTermGoals: '',
        websiteUrl: '',
        prospectingProfile: {
            currentRole: '',
            companyPurpose: '',
            targetAudience: '',
            howToBePerceived: '',
            currentMarketingStrategy: '',
            contentIdeas: '',
            instagramUrl: '',
            inspiringBrands: '',
            competitors: '',
            targetChallenge: ''
        }
    });

    const [userInfo, setUserInfo] = useState({
        picture: '',
        name: ''
    });

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [hasVisited, setHasVisited] = useState(true);

    // Navigation (for redirecting to landing page)
    const [hasCreatedFirstVideo, setHasCreatedFirstVideo] = useState(false);

    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState(false);
    const [subscriptionError, setSubscriptionError] = useState('');
    const [isBetaUser, setIsBetaUser] = useState(false);
    const [backendError, setBackendError] = useState('');

    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info'); // Can be 'info', 'error', or 'success'

    // Voice cloning state variables - moved to ProfileModal component

    const {
        videoPrompt, setVideoPrompt,
        selectedCategories, setSelectedCategories,
        generatedVideoUrl, setGeneratedVideoUrl,
      } = useWorkspace();

    const [showMetaModal, setShowMetaModal] = useState(false);

    const [availableInterests, setAvailableInterests] = useState([]);
    const { 
        selectedInterests,
        videoCards,
        analyzing,
        sourceVideo,
        selectedModel,
        credits,
        handleInterestSelect,
        updateCardPrompt,
        generateVideo,
        generateAllVideos,
        downloadVideo,
        setVideo,
        setModel,
        clearErrors,
    } = useVideoGeneration();

    const [generatedVideos, setGeneratedVideos] = useState([]);

    useEffect(() => {
        localStorage.setItem('preferredVideoModel', selectedModel);
    }, [selectedModel]);



    // Fetch user info on component mount
    useEffect(() => {
        const initializeUser = async () => {
            const params = new URLSearchParams(window.location.search);
            const tokenFromUrl = params.get('token');
            let token = tokenFromUrl || localStorage.getItem('token');

            console.log("params:", params);
            console.log("tokenFromUrl:", tokenFromUrl);
            console.log('Token from localStorage:', localStorage.getItem('token').substring(0, 10));
            console.log("token:", token.substring(0, 10));

            if (tokenFromUrl) {
                // Save the token to local storage
                localStorage.setItem('token', tokenFromUrl);
                console.log('Token saved to localStorage:', tokenFromUrl);
                
                // Clean up the URL to remove the token
                window.history.replaceState({}, document.title, window.location.pathname);
            }

            // Validate the token
            if (!token || isTokenExpired(token)) {
                console.log('Token is invalid or expired. Redirecting to landing page.');
                navigate('/');
                return;
            }

            // Decode the token to get the user ID and fetch files
            const decoded = decodeToken(token);
            console.log('decoded Token:', decoded);
            console.log('decoded username:', decoded.name);
            console.log('decoded ID:', decoded.userId);

            if (decoded) {
                setUserId(decoded.userId); // Set the user ID
                setUserName(decoded.name); // Extract the name from the token
                setUserInfo(prev => ({ ...prev, name: decoded.name }));

                try {
                    setBackendError(''); // Clear any previous error

                    // Fetch profile
                    const profileResponse = await api.getUserProfile();
                    console.log('Profile response:', profileResponse.data);

                    if (profileResponse.data) {
                        setUserProfile({
                            ...profileResponse.data.profile,
                            websiteUrl: profileResponse.data.profile?.websiteUrl || '',
                            prospectingProfile: profileResponse.data.prospectingProfile || {
                                currentRole: '',
                                companyPurpose: '',
                                targetAudience: ''
                            },
                            socialMedia: profileResponse.data.socialMedia || {
                                mainPlatform: 'instagram',
                                instagram: { handle: '', currentFollowers: 0, targetFollowers: 0 },
                                tiktok: { handle: '', currentFollowers: 0, targetFollowers: 0 },
                                youtube: { handle: '', currentFollowers: 0, targetFollowers: 0 }
                            },
                            videoProduction: profileResponse.data.videoProduction || {
                                currentVideosPerMonth: 0,
                                targetVideosPerMonth: 0
                            },
                            hasCreatedFirstVideo: profileResponse.data.hasCreatedFirstVideo,
                            isBetaUser: profileResponse.data.isBetaUser,
                            isSubscribed: profileResponse.data.isSubscribed,
                            stripeCustomerId: profileResponse.data.stripeCustomerId,
                            subscriptionStatus: profileResponse.data.subscriptionStatus
                        });

                        setProfileComplete(profileResponse.data.profileComplete);
                        setHasVisited(profileResponse.data.hasVisited);
                        setUserInfo(prev => ({
                            ...prev,
                            picture: profileResponse.data.picture,
                            name: profileResponse.data.name || decoded.name
                        }));
                        
                        if (!profileResponse.data.hasVisited) {
                            setShowProfileModal(true);
                            await api.markVisited();
                        }
                    }

                } catch (error) {
                    console.error('Error initializing:', error);
                    setBackendError('Hey people, Boukar here, I was coding and did some shit, server down, will fix asap, sorry for that, thanks for using the app (help!)');
                    if (error.response?.status === 401) {
                        navigate('/');
                    }
                }
            } else {
                setBackendError('Hey people, Boukar here, I was coding and did some shit, server down, will fix asap, sorry for that, thanks for using the app (help!)');//setBackendError('Unable to load data, server down, please try again later');
            }
        };

        initializeUser();
    }, [navigate]);

    // Profile Modal Component
    const ProfileModal = () => {
        // Voice cloning state variables - moved inside ProfileModal
        const [isRecording, setIsRecording] = useState(false);
        const [recordings, setRecordings] = useState([]);
        const [mediaRecorder, setMediaRecorder] = useState(null);
        const [mediaStream, setMediaStream] = useState(null);
        const [isGeneratingVoice, setIsGeneratingVoice] = useState(false);
        const [isTestingVoice, setIsTestingVoice] = useState(false);
        const [audioUrl, setAudioUrl] = useState('');
        const [isPlayingVoice, setIsPlayingVoice] = useState(false);
        const audioRef = useRef(null);
        const [recordingDuration, setRecordingDuration] = useState(0);
        const [timerInterval, setTimerInterval] = useState(null);
        const [isSavingRecording, setIsSavingRecording] = useState(false);
        const [recordingLength, setRecordingLength] = useState({});
        const [showRecordingSection, setShowRecordingSection] = useState(false);
        const [showExamples, setShowExamples] = useState(false);
        const [currentExample, setCurrentExample] = useState(0);
        const [displayedExamples, setDisplayedExamples] = useState([]);
        const [isSaving, setIsSaving] = useState(false);
        const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
        const [userVoiceId, setUserVoiceId] = useState(null);
        const [testAudioUrl, setTestAudioUrl] = useState('');
        const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
        const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
        const [hasSavedToBackend, setHasSavedToBackend] = useState(false);
        const [savedFormData, setSavedFormData] = useState(null);

        // État local pour le formulaire
        const [formData, setFormData] = useState({
            currentSituation: userProfile.currentSituation || '',
            shortTermGoals: userProfile.shortTermGoals || '',
            longTermGoals: userProfile.longTermGoals || '',
            websiteUrl: userProfile.websiteUrl || '',
            prospectingProfile: {
                currentRole: userProfile.prospectingProfile?.currentRole || '',
                companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                competitors: userProfile.prospectingProfile?.competitors || '',
                targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
            },
            socialMedia: {
                mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                instagram: {
                    handle: userProfile.socialMedia?.instagram?.handle || '',
                    currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                },
                tiktok: {
                    handle: userProfile.socialMedia?.tiktok?.handle || '',
                    currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                },
                youtube: {
                    handle: userProfile.socialMedia?.youtube?.handle || '',
                    currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                    targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                }
            },
            videoProduction: {
                currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
            }
        });

        // Initialize formData and savedFormData when modal opens (only once)
        useEffect(() => {
            const initialData = {
                currentSituation: userProfile.currentSituation || '',
                shortTermGoals: userProfile.shortTermGoals || '',
                longTermGoals: userProfile.longTermGoals || '',
                websiteUrl: userProfile.websiteUrl || '',
                prospectingProfile: {
                    currentRole: userProfile.prospectingProfile?.currentRole || '',
                    companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                    targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                    howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                    instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                    competitors: userProfile.prospectingProfile?.competitors || '',
                    targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: userProfile.socialMedia?.instagram?.handle || '',
                        currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: userProfile.socialMedia?.tiktok?.handle || '',
                        currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: userProfile.socialMedia?.youtube?.handle || '',
                        currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
                }
            };
            
            setFormData(initialData);
            setSavedFormData(initialData); // Set initial baseline
        }, []); // Empty dependency array - only run once when modal opens

        // Save to backend only (no parent state update)
        const saveToBackend = async () => {
            try {
                setIsSaving(true);
                setSaveStatus('saving');
                
                trackEvent('profile_update_started', {
                    userId,
                    fieldsUpdated: [
                        ...Object.keys(formData).filter(key => 
                            key !== 'prospectingProfile' && 
                            key !== 'socialMedia' && 
                            formData[key] && 
                            typeof formData[key] === 'string' && 
                            formData[key].trim() !== ''
                        ),
                        ...Object.keys(formData.prospectingProfile).filter(key => 
                            formData.prospectingProfile[key] && 
                            typeof formData.prospectingProfile[key] === 'string' && 
                            formData.prospectingProfile[key].trim() !== ''
                        )
                    ]
                });

                // Structure the data correctly for the API
                const profileData = {
                    profile: {
                        currentSituation: formData.currentSituation,
                        shortTermGoals: formData.shortTermGoals,
                        longTermGoals: formData.longTermGoals,
                        websiteUrl: formData.websiteUrl
                    }
                };

                // Update main profile
                const profileResponse = await api.updateProfile(profileData);

                // Update prospecting profile separately
                const prospectingData = {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge,
                    socialMedia: formData.socialMedia,
                    videoProduction: formData.videoProduction
                };
                
                await api.updateProspectingProfile(prospectingData);

                trackEvent('profile_update_successful', {
                    userId,
                    profileComplete: profileResponse.data.profileComplete
                });

                // Clear unsaved changes flag and mark as saved to backend
                setHasUnsavedChanges(false);
                setHasSavedToBackend(true);
                
                // Update the saved baseline to current form data
                setSavedFormData({
                    currentSituation: formData.currentSituation,
                    shortTermGoals: formData.shortTermGoals,
                    longTermGoals: formData.longTermGoals,
                    websiteUrl: formData.websiteUrl,
                    prospectingProfile: {
                        currentRole: formData.prospectingProfile.currentRole,
                        companyPurpose: formData.prospectingProfile.companyPurpose,
                        targetAudience: formData.prospectingProfile.targetAudience,
                        howToBePerceived: formData.prospectingProfile.howToBePerceived,
                        currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                        contentIdeas: formData.prospectingProfile.contentIdeas,
                        instagramUrl: formData.prospectingProfile.instagramUrl,
                        inspiringBrands: formData.prospectingProfile.inspiringBrands,
                        competitors: formData.prospectingProfile.competitors,
                        targetChallenge: formData.prospectingProfile.targetChallenge
                    },
                    socialMedia: formData.socialMedia,
                    videoProduction: formData.videoProduction
                });
                
                // Show success status
                setSaveStatus('saved');
                setIsSaving(false);
                
                // Reset status after 3 seconds
                setTimeout(() => {
                    setSaveStatus('');
                }, 3000);
                
                return true; // Indicate success
                
            } catch (error) {
                trackEvent('profile_update_error', {
                    error: error.message,
                    userId
                });
                console.error('Error saving profile:', error);
                setSaveStatus('error');
                setIsSaving(false);
                
                // Reset error status after 5 seconds
                setTimeout(() => {
                    setSaveStatus('');
                }, 5000);
                
                throw error; // Re-throw to be caught by caller
            }
        };

        // Update parent state (causes re-render)
        const updateParentState = () => {
            setUserProfile(prev => ({
                ...prev,
                currentSituation: formData.currentSituation,
                shortTermGoals: formData.shortTermGoals,
                longTermGoals: formData.longTermGoals,
                websiteUrl: formData.websiteUrl,
                prospectingProfile: {
                    currentRole: formData.prospectingProfile.currentRole,
                    companyPurpose: formData.prospectingProfile.companyPurpose,
                    targetAudience: formData.prospectingProfile.targetAudience,
                    howToBePerceived: formData.prospectingProfile.howToBePerceived,
                    currentMarketingStrategy: formData.prospectingProfile.currentMarketingStrategy,
                    contentIdeas: formData.prospectingProfile.contentIdeas,
                    instagramUrl: formData.prospectingProfile.instagramUrl,
                    inspiringBrands: formData.prospectingProfile.inspiringBrands,
                    competitors: formData.prospectingProfile.competitors,
                    targetChallenge: formData.prospectingProfile.targetChallenge
                },
                socialMedia: formData.socialMedia,
                videoProduction: formData.videoProduction
            }));
        };

        // Main save function (backend only)
        const handleSave = async () => {
            return await saveToBackend();
        };

        const handleManageSubscription = async () => {
            try {
                const response = await api.createPortalSession();
                if (response.data.url) {
                    window.open(response.data.url, '_blank');
                }
            } catch (error) {
                console.error('Error opening subscription portal:', error);
                // Optionally show an error message to the user
            }
        };

        // Voice cloning useEffect hooks - moved inside ProfileModal
        // Initialize random examples when showing them
        useEffect(() => {
            if (showExamples) {
                // Shuffle array and take first 10 examples
                const shuffled = [...RECORDING_EXAMPLES]
                    .sort(() => Math.random() - 0.5)
                    .slice(0, 10);
                setDisplayedExamples(shuffled);
                setCurrentExample(0);
            }
        }, [showExamples]);

        // Rotate through examples
        useEffect(() => {
            if (showExamples && displayedExamples.length > 0) {
                const interval = setInterval(() => {
                    setCurrentExample(prev => (prev + 1) % displayedExamples.length);
                }, 3000);
                return () => clearInterval(interval);
            }
        }, [showExamples, displayedExamples]);

        // Fetch user voice data
        useEffect(() => {
            const fetchUserVoiceData = async () => {
                try {
                    const response = await api.getUserProfile();
                    console.log('Full user data:', response.data);
                    const userData = response.data;
                    
                    if (userData.voiceId) {
                        console.log('Found voice ID:', userData.voiceId);
                        setUserVoiceId(userData.voiceId);
                        setShowRecordingSection(false);
                    } else {
                        console.log('No voice ID found in user data');
                        setShowRecordingSection(true);
                    }

                    if (response.data.recordings) {
                        setRecordings(response.data.recordings);
                        console.log('Recordings loaded:', response.data.recordings);
                    }

                    // Initialize test audio URL from user data
                    if (userData.testAudio) {
                        setTestAudioUrl(userData.testAudio);
                        console.log('Test audio URL loaded:', userData.testAudio);
                    }

                    console.log('User voice ID:', userData.voiceId);

                    trackEvent('user_voice_data_fetched', {
                        userId,
                        voiceId: userData.voiceId,
                        hasRecordings: response.data.recordings ? response.data.recordings.length > 0 : false,
                        hasTestAudio: !!userData.testAudio
                    });

                } catch (error) {
                    console.error('Error fetching user voice data:', error);
                }
            };
        
            // Always fetch when modal opens, not just when userId changes
            if (userId) {
                fetchUserVoiceData();
            }
        }, [userId]);

        // Voice cloning utility functions - moved inside ProfileModal
        const formatTime = (seconds) => {
            if (typeof seconds === 'string') {
                return seconds;
            }
            const mins = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        // Initialize voice recorder
        const initializeRecorder = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                setMediaStream(stream);

                const recorder = new MediaRecorder(stream, {
                    mimeType: 'audio/webm'
                });
        
                const chunks = [];
                recorder.ondataavailable = (e) => {
                    if (e.data.size > 0) {
                        chunks.push(e.data);
                    }
                };

                recorder.onstop = async () => {
                    const audioBlob = new Blob(chunks, { type: 'audio/webm' });
                    const finalDuration = recordingDuration; // Capture duration immediately
                    console.log('Final recording duration:', recordingDuration); // Should show actual duration
                    console.log('Final finalDuration:', finalDuration);

                    try {
                        // Create FormData and append both audio and duration
                        const formData = new FormData();
                        formData.append('audio', audioBlob, 'recording.webm');
                        formData.append('duration', finalDuration); // Use captured duration
                        console.log('FormData duration:', formData.get('duration'));

                        const response = await api.saveRecording(formData);
                        console.log('Recording saved:', response.data);
                        if (response.data.success) {
                            setRecordings(prev => [...prev, response.data.recording]);
                            // Store the duration in local state
                            setRecordingLength(prev => ({
                                ...prev,
                                [response.data.recording._id]: finalDuration
                            }));
                        }
                    } catch (error) {
                        console.error('Error saving recording:', error);
                    } finally {
                        setIsSavingRecording(false);
                        setIsRecording(false);
                    }
                    chunks.length = 0; // Clear the chunks array
                };

                setMediaRecorder(recorder);
                return recorder;
            } catch (error) {
                console.error('Error accessing microphone:', error);
                return null;
            }
        };

        // Start recording
        const startRecording = async () => {
            if (recordings.length >= 5) {
                alert('Maximum number of recordings reached');
                return;
            }

            try {
                setIsRecording(true);
                setIsSavingRecording(false);
                setRecordingDuration(0);

                const recorder = await initializeRecorder();
                if (!recorder) {
                    setIsRecording(false);
                    return;
                }

                recorder.start();
                setMediaRecorder(recorder);

                // Start timer
                const interval = setInterval(() => {
                    setRecordingDuration(prev => prev + 1);
                }, 1000);
                setTimerInterval(interval);

            } catch (error) {
                console.error('Error starting recording:', error);
                setIsRecording(false);
            }
        };

        // Stop recording
        const stopRecording = () => {
            if (mediaRecorder && mediaRecorder.state === 'recording') {
                setIsSavingRecording(true);
                mediaRecorder.stop();
                
                // Stop timer
                if (timerInterval) {
                    clearInterval(timerInterval);
                    setTimerInterval(null);
                }

                // Stop all tracks
                if (mediaStream) {
                    mediaStream.getTracks().forEach(track => track.stop());
                }
            }
        };

        // Handle voice cloning
        const handleVoiceCloning = async () => {
            try {
                setIsGeneratingVoice(true);
                
                console.log('Recordings to send:', recordings);
                
                if (!recordings || recordings.length === 0) {
                    throw new Error('No recordings found. Please record some samples first.');
                }
        
                // Create FormData
                const formData = new FormData();
                
                // Append each recording URL as a separate field
                recordings.forEach((recording, index) => {
                    formData.append(`recording${index}`, recording.url);
                });
        
                // Add the total number of recordings
                formData.append('recordingCount', recordings.length);
        
                // For debugging - log the FormData contents
                for (let pair of formData.entries()) {
                    console.log(pair[0], pair[1]);
                }
        
                // Clone the voice using ElevenLabs API
                const cloneResponse = await api.cloneVoice(formData);
        
                console.log('Voice cloning response:', cloneResponse.data);
                
                if (cloneResponse.data && cloneResponse.data.voice_id) {
                    setUserVoiceId(cloneResponse.data.voice_id);
                    await api.updateProfile({ voiceId: cloneResponse.data.voice_id });
                    alert('Voice cloned successfully!');
                }
                trackEvent('voice_cloning_completed', {
                    userId,
                    voiceId: cloneResponse.data.voice_id
                });
            } catch (error) {
                console.error('Error cloning voice:', error);
                alert(error.message || 'Failed to clone voice. Please try again.');
                trackEvent('voice_cloning_error', {
                    userId,
                    error: error.message
                });
            } finally {
                setIsGeneratingVoice(false);
            }
        };

        // Test cloned voice
        const testClonedVoice = async () => {
            if (!userVoiceId) {
                alert('Please clone your voice first');
                return;
            }
        
            try {
                setIsTestingVoice(true);
                const response = await api.testClonedVoice(
                    "Hello! This is my cloned voice. How does it sound?",
                    userVoiceId
                );
                
                // The response will now contain the S3 URL
                if (response.data && response.data.audioUrl) {
                    setTestAudioUrl(response.data.audioUrl); // Update this line
                }
                trackEvent('voice_test_completed', {
                    userId,
                    voiceId: userVoiceId,
                    audioUrl: response.data.audioUrl
                });
            } catch (error) {
                console.error('Error testing cloned voice:', error);
                alert('Failed to test voice. Please try again.');
                trackEvent('voice_test_error', {
                    userId,
                    error: error.message
                });
            } finally {
                setIsTestingVoice(false);
            }
        };

        // Handle delete recording
        const handleDeleteRecording = async (recordingId) => {
            try {
                await api.deleteRecording(recordingId);
                setRecordings(prev => prev.filter(recording => recording._id !== recordingId));
                
                // Remove from recording length state
                setRecordingLength(prev => {
                    const newState = { ...prev };
                    delete newState[recordingId];
                    return newState;
                });
            } catch (error) {
                console.error('Error deleting recording:', error);
                alert('Failed to delete recording. Please try again.');
            }
        };

        const handleLogout = () => {
            // Clear local storage
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            
            // Track logout event
            trackEvent('user_logged_out', { userId });
            
            // Redirect to home
            window.location.href = '/';
        };

        const handleCloseAttempt = () => {
            // Check if there are unsaved changes
            if (checkForChanges()) {
                setShowSaveConfirmation(true);
            } else {
                // No changes, close directly
                handleDirectClose();
            }
        };

        const handleConfirmClose = () => {
            setShowSaveConfirmation(false);
            
            // If changes were saved to backend, update parent state before closing
            if (hasSavedToBackend) {
                updateParentState();
            }
            
            setShowProfileModal(false);
        };

        // Handle direct close (no confirmation needed)
        const handleDirectClose = () => {
            // If changes were saved to backend, update parent state before closing
            if (hasSavedToBackend) {
                updateParentState();
            }
            
            setShowProfileModal(false);
        };

        const handleSaveAndClose = async () => {
            try {
                setShowSaveConfirmation(false);
                
                // Save to backend first
                const saveSuccess = await saveToBackend();
                
                if (saveSuccess) {
                    // Update parent state and close modal
                    updateParentState();
                    setShowProfileModal(false);
                }
                // If save fails, the modal stays open and error is shown
            } catch (error) {
                console.error('Error saving and closing:', error);
                // Keep the modal open if save fails
            }
        };

        const handleCancelClose = () => {
            setShowSaveConfirmation(false);
        };

        // Check if there are unsaved changes
        const checkForChanges = () => {
            // Use savedFormData as baseline if available, otherwise use userProfile
            const baseline = savedFormData || {
                currentSituation: userProfile.currentSituation || '',
                shortTermGoals: userProfile.shortTermGoals || '',
                longTermGoals: userProfile.longTermGoals || '',
                websiteUrl: userProfile.websiteUrl || '',
                prospectingProfile: {
                    currentRole: userProfile.prospectingProfile?.currentRole || '',
                    companyPurpose: userProfile.prospectingProfile?.companyPurpose || '',
                    targetAudience: userProfile.prospectingProfile?.targetAudience || '',
                    howToBePerceived: userProfile.prospectingProfile?.howToBePerceived || '',
                    currentMarketingStrategy: userProfile.prospectingProfile?.currentMarketingStrategy || '',
                    contentIdeas: userProfile.prospectingProfile?.contentIdeas || '',
                    instagramUrl: userProfile.prospectingProfile?.instagramUrl || '',
                    inspiringBrands: userProfile.prospectingProfile?.inspiringBrands || '',
                    competitors: userProfile.prospectingProfile?.competitors || '',
                    targetChallenge: userProfile.prospectingProfile?.targetChallenge || ''
                },
                socialMedia: {
                    mainPlatform: userProfile.socialMedia?.mainPlatform || 'instagram',
                    instagram: {
                        handle: userProfile.socialMedia?.instagram?.handle || '',
                        currentFollowers: userProfile.socialMedia?.instagram?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.instagram?.targetFollowers || 0
                    },
                    tiktok: {
                        handle: userProfile.socialMedia?.tiktok?.handle || '',
                        currentFollowers: userProfile.socialMedia?.tiktok?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.tiktok?.targetFollowers || 0
                    },
                    youtube: {
                        handle: userProfile.socialMedia?.youtube?.handle || '',
                        currentFollowers: userProfile.socialMedia?.youtube?.currentFollowers || 0,
                        targetFollowers: userProfile.socialMedia?.youtube?.targetFollowers || 0
                    }
                },
                videoProduction: {
                    currentVideosPerMonth: userProfile.videoProduction?.currentVideosPerMonth || 0,
                    targetVideosPerMonth: userProfile.videoProduction?.targetVideosPerMonth || 0
                }
            };
            
            const hasChanges = 
                formData.currentSituation !== baseline.currentSituation ||
                formData.shortTermGoals !== baseline.shortTermGoals ||
                formData.longTermGoals !== baseline.longTermGoals ||
                formData.websiteUrl !== baseline.websiteUrl ||
                formData.prospectingProfile.currentRole !== baseline.prospectingProfile.currentRole ||
                formData.prospectingProfile.companyPurpose !== baseline.prospectingProfile.companyPurpose ||
                formData.prospectingProfile.targetAudience !== baseline.prospectingProfile.targetAudience ||
                formData.prospectingProfile.howToBePerceived !== baseline.prospectingProfile.howToBePerceived ||
                formData.prospectingProfile.currentMarketingStrategy !== baseline.prospectingProfile.currentMarketingStrategy ||
                formData.prospectingProfile.contentIdeas !== baseline.prospectingProfile.contentIdeas ||
                formData.prospectingProfile.instagramUrl !== baseline.prospectingProfile.instagramUrl ||
                formData.prospectingProfile.inspiringBrands !== baseline.prospectingProfile.inspiringBrands ||
                formData.prospectingProfile.competitors !== baseline.prospectingProfile.competitors ||
                formData.prospectingProfile.targetChallenge !== baseline.prospectingProfile.targetChallenge ||
                formData.socialMedia.mainPlatform !== baseline.socialMedia.mainPlatform ||
                formData.socialMedia.instagram.handle !== baseline.socialMedia.instagram.handle ||
                formData.socialMedia.instagram.currentFollowers !== baseline.socialMedia.instagram.currentFollowers ||
                formData.socialMedia.instagram.targetFollowers !== baseline.socialMedia.instagram.targetFollowers ||
                formData.socialMedia.tiktok.handle !== baseline.socialMedia.tiktok.handle ||
                formData.socialMedia.tiktok.currentFollowers !== baseline.socialMedia.tiktok.currentFollowers ||
                formData.socialMedia.tiktok.targetFollowers !== baseline.socialMedia.tiktok.targetFollowers ||
                formData.socialMedia.youtube.handle !== baseline.socialMedia.youtube.handle ||
                formData.socialMedia.youtube.currentFollowers !== baseline.socialMedia.youtube.currentFollowers ||
                formData.socialMedia.youtube.targetFollowers !== baseline.socialMedia.youtube.targetFollowers ||
                formData.videoProduction.currentVideosPerMonth !== baseline.videoProduction.currentVideosPerMonth ||
                formData.videoProduction.targetVideosPerMonth !== baseline.videoProduction.targetVideosPerMonth;
            
            setHasUnsavedChanges(hasChanges);
            return hasChanges;
        };

        // Update hasUnsavedChanges whenever formData changes
        useEffect(() => {
            const hasChanges = checkForChanges();
            
            // If there are new changes, reset the saved to backend flag
            if (hasChanges) {
                setHasSavedToBackend(false);
            }
        }, [formData]);

        return (
            <div 
                style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 2000
                }}
                onClick={handleCloseAttempt}
            >
                <div 
                    style={{
                    backgroundColor: darkMode ? '#27292D' : 'white',
                    padding: '30px',
                    borderRadius: '10px',
                    width: '90%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    overflowY: 'auto'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2>Hi {(userInfo.name || userName)?.split(' ')[0]}!</h2>
                    <button
                        onClick={handleLogout}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '5px',
                            // backgroundColor: '#f0f0f0',
                            border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            color: '#8B0000' //'#dc3545'  // Red color for logout
                        }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                            <polyline points="16 17 21 12 16 7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Logout
                    </button>
                    {!userProfile.isSubscribed && (
                        <button
                            onClick={handleManageSubscription}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '5px',
                                // backgroundColor: '#f0f0f0',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginTop: '10px'
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 10h18M7 15h.01M11 15h.01M15 15h.01M19 15h.01M5 20h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"/>
                            </svg>
                            Manage Subscription
                        </button>
                    )}
                    <h2>Tell us more about you</h2>
                    <p style={{ color: '#666', marginBottom: '20px' }}>
                        This information helps us provide better personalized assistance
                    </p>
                    {!hasVisited && (
                        <p style={{ color: '#007bff', marginBottom: '20px' }}>
                            You can change it whenever.
                        </p>
                    )}

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Situation
                        </label>
                        <TextareaAutosize
                            value={formData.currentSituation}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                currentSituation: e.target.value
                            }))}
                            placeholder="Your current job, skills, experience..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Short Term Goals
                        </label>
                        <TextareaAutosize
                            value={formData.shortTermGoals}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                shortTermGoals: e.target.value
                            }))}
                            placeholder="Goals for the next 3-6 months..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Long Term Goals
                        </label>
                        <TextareaAutosize
                            value={formData.longTermGoals}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                longTermGoals: e.target.value
                            }))}
                            placeholder="Where do you see yourself in 1-5 years..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Website URL
                        </label>
                        <input
                            type="url"
                            value={formData.websiteUrl}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                websiteUrl: e.target.value
                            }))}
                            placeholder="https://your-website.com"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Professional Profile
                    </h3>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Role
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.currentRole}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    currentRole: e.target.value
                                }
                            }))}
                            placeholder="Your current role..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Company Purpose
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.companyPurpose}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    companyPurpose: e.target.value
                                }
                            }))}
                            placeholder="Your company's purpose..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Target Audience
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.targetAudience}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    targetAudience: e.target.value
                                }
                            }))}
                            placeholder="Your target audience..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            How do you want to be perceived?
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.howToBePerceived}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    howToBePerceived: e.target.value
                                }
                            }))}
                            placeholder="How do you want your audience to perceive you and your brand..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Marketing Strategy
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.currentMarketingStrategy}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    currentMarketingStrategy: e.target.value
                                }
                            }))}
                            placeholder="Describe your current marketing approach and strategies..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Content Ideas
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.contentIdeas}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    contentIdeas: e.target.value
                                }
                            }))}
                            placeholder="What types of content do you want to create? Any specific ideas or themes..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Link to your Instagram
                        </label>
                        <input
                            type="url"
                            value={formData.prospectingProfile.instagramUrl}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    instagramUrl: e.target.value
                                }
                            }))}
                            placeholder="https://instagram.com/yourusername"
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                color: darkMode ? '#fff' : '#333',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Brands / Accounts that inspire you
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.inspiringBrands}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    inspiringBrands: e.target.value
                                }
                            }))}
                            placeholder="List brands, influencers, or accounts that inspire your content and business approach..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Competitors and main differentiation
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.competitors}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    competitors: e.target.value
                                }
                            }))}
                            placeholder="Who are your main competitors and what makes you different from them..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Your target's main challenge and how you solve them
                        </label>
                        <TextareaAutosize
                            value={formData.prospectingProfile.targetChallenge}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                prospectingProfile: {
                                    ...prev.prospectingProfile,
                                    targetChallenge: e.target.value
                                }
                            }))}
                            placeholder="What are the main challenges your target audience faces and how do you help solve them..."
                            minRows={3}
                            maxRows={8}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                fontFamily: 'inherit',
                                boxSizing: 'border-box',
                                resize: 'none'
                            }}
                        />
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Social Media & Goals
                    </h3>

                    {/* Main Platform Selection */}
                    <div style={{ marginBottom: '25px' }}>
                        <label style={{ 
                            display: 'block', 
                            marginBottom: '10px', 
                            fontSize: '16px', 
                            fontWeight: 'bold',
                            color: darkMode ? '#fff' : '#333'
                        }}>Main Social Media Platform</label>
                        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* Custom Radio Button for Instagram */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'instagram'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'instagram' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>Instagram</span>
                            </label>

                            {/* Custom Radio Button for TikTok */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'tiktok'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'tiktok' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>TikTok</span>
                            </label>

                            {/* Custom Radio Button for YouTube */}
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                <div
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        socialMedia: {
                                            ...prev.socialMedia,
                                            mainPlatform: 'youtube'
                                        }
                                    }))}
                                    style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        border: `2px solid ${darkMode ? '#6366f1' : '#1976d2'}`,
                                        backgroundColor: darkMode ? '#2A2B2F' : '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    {formData.socialMedia.mainPlatform === 'youtube' && (
                                        <div style={{
                                            width: '7px',
                                            height: '7px',
                                            borderRadius: '50%',
                                            backgroundColor: darkMode ? '#6366f1' : '#1976d2'
                                        }} />
                                    )}
                                </div>
                                <span style={{ 
                                    fontSize: '14px',
                                    color: darkMode ? '#fff' : '#333'
                                }}>YouTube</span>
                            </label>
                        </div>
                    </div>

                    {/* Instagram */}
                    <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Instagram</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.instagram.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.instagram.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.instagram.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        instagram: {
                                            ...prev.socialMedia.instagram,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* TikTok */}
                    <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>TikTok</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.tiktok.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.tiktok.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.tiktok.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        tiktok: {
                                            ...prev.socialMedia.tiktok,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* YouTube */}
                    <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>YouTube</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Handle</label>
                            <input
                                type="text"
                                placeholder="@username"
                                value={formData.socialMedia.youtube.handle}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            handle: e.target.value
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Followers</label>
                            <input
                                value={formData.socialMedia.youtube.currentFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            currentFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Followers</label>
                            <input
                                value={formData.socialMedia.youtube.targetFollowers}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    socialMedia: {
                                        ...prev.socialMedia,
                                        youtube: {
                                            ...prev.socialMedia.youtube,
                                            targetFollowers: parseInt(e.target.value) || 0
                                        }
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    {/* Video Production */}
                    <h4 style={{ marginBottom: '15px', color: darkMode ? '#fff' : '#333' }}>Video Production Goals</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '30px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Current Videos Per Month</label>
                            <input
                                value={formData.videoProduction.currentVideosPerMonth}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    videoProduction: {
                                        ...prev.videoProduction,
                                        currentVideosPerMonth: parseInt(e.target.value) || 0
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Target Videos Per Month</label>
                            <input
                                value={formData.videoProduction.targetVideosPerMonth}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    videoProduction: {
                                        ...prev.videoProduction,
                                        targetVideosPerMonth: parseInt(e.target.value) || 0
                                    }
                                }))}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                    backgroundColor: darkMode ? '#2A2B2F' : 'white',
                                    color: darkMode ? '#fff' : '#333',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                    </div>

                    <h3 style={{ 
                        marginTop: '30px', 
                        marginBottom: '20px', 
                        color: darkMode ? '#fff' : '#333',
                        borderBottom: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                        paddingBottom: '10px'
                    }}>
                        Voice Cloning
                    </h3>

                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ color: '#666', marginBottom: '20px' }}>
                            Record your voice to create a digital voice clone.
                        </p>
                        <button
                            onClick={() => setShowRecordingSection(!showRecordingSection)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: darkMode ? '#2A2B2F' : '#f8f9fa',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                borderRadius: '4px',
                                color: darkMode ? '#ffffff95' : '#666',
                                cursor: 'pointer',
                                marginBottom: '20px',
                                width: '180px'
                            }}
                        >
                            {showRecordingSection ? (
                                <>
                                    <ChevronUp size={16} />
                                    Hide Voice Cloning
                                </>
                            ) : (
                                <>
                                    <ChevronDown size={16} />
                                    Show Voice Cloning
                                </>
                            )}
                        </button>
                        {showRecordingSection && (
                        <div>
                        <div style={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: '10px' 
                        }}>
                            
                            <p style={{ fontSize:'14px', marginBottom: '10px', marginTop:'0px', color: '#666' }}>
                                Record at least 1 sample to clone your voice. 
                                For best results, record 3-5 samples, each 10-30 seconds long.
                            </p>
                            <button
                                onClick={() => setShowExamples(!showExamples)}
                                style={{
                                    padding: '2px 12px',
                                    backgroundColor: 'transparent',
                                    color: '#1a73e8',
                                    border: '1px solid #1a73e8',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    marginLeft: '16px',
                                }}
                            >
                                {showExamples ? 'Hide inspiration' : 'Click for inspiration'}
                            </button>
                        </div>

                        {/* Example Sentences */}
                        {showExamples ? (
                            <div style={{
                                padding: '12px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                marginBottom: '16px',
                                border: '1px solid #dfe1e5',
                                position: 'relative',
                                overflow: 'hidden',
                                height: '60px' // Fixed height to prevent layout shifts
                            }}>
                                {displayedExamples.map((example, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '12px',
                                            right: '12px',
                                            transform: `translateY(-50%) translateY(${(index - currentExample) * 60}px)`,
                                            opacity: index === currentExample ? 1 : 0,
                                            transition: 'all 0.5s ease-in-out',
                                            textAlign: 'center',
                                            color: '#666'
                                        }}
                                    >
                                        "{example}"
                                    </div>
                                ))}
                            </div>
                        ):(
                            <p style={{fontSize:'14px', marginTop:'0px', marginBottom:'30px', color:'#666'}}>Not sure what to say? Click for inspiration.</p>
                        )}

                        {/* Progress Bar */}
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                marginBottom: '8px',
                                fontSize: '0.9em',
                                color: '#666'
                            }}>
                                <span>Recording Progress</span>
                                <span>{recordings.length} recording{recordings.length !== 1 ? 's' : ''} {recordings.length >= 1 ? '(Ready to clone)' : '(Need at least 1)'}</span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${Math.min((recordings.length / 5) * 100, 100)}%`,
                                    height: '100%',
                                    background: 'linear-gradient(to right, #AB5898 50%, #5C4BD3 80%)',
                                    transition: 'width 0.3s ease',
                                    borderRadius: '4px',
                                }} />
                            </div>
                            {recordings.length < 1 && (
                                <p style={{ 
                                    fontSize: '0.8em', 
                                    color: '#666', 
                                    marginTop: '8px' 
                                }}>
                                    At least 1 recording needed to clone your voice
                                </p>
                            )}
                            {recordings.length >= 1 && recordings.length < 5 && (
                                <p style={{ 
                                    fontSize: '0.8em', 
                                    color: '#666', 
                                    marginTop: '8px' 
                                }}>
                                    {5 - recordings.length} more {5 - recordings.length === 1 ? 'recording' : 'recordings'} for best results
                                </p>
                            )}
                        </div>
                        
                        {/* Recording controls */}
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <button
                                onClick={isRecording ? stopRecording : startRecording}
                                className={`main-button ${isRecording ? 'recording' : ''}`}
                                style={{ 
                                    flex: 1,
                                    backgroundColor: (recordings.length >= 5 ? '#d9df70' : (isRecording ? (isSavingRecording ? '#ccc' : '#dc3545') : '#e1f679')),
                                    cursor: recordings.length >= 5 ? 'not-allowed' : 'pointer'
                                }}
                                disabled={recordings.length >= 5 && !isRecording}
                            >
                                {isRecording ? (
                                    <>
                                        {isSavingRecording ? <Loader size={16} style={{ marginRight: '5px' }} /> : <Square size={16} style={{ marginRight: '5px' }} />}
                                        {isSavingRecording ? 'Saving...' : <>'Stop Recording' ({formatTime(recordingDuration)})</>}
                                    </>
                                ) : (
                                    <>
                                        {recordings.length >=5 ? <Check size={16} style={{ marginRight: '5px' }} /> : <Mic size={16} style={{ marginRight: '5px' }} />}
                                        {recordings.length >= 5 ? 'Max Recordings Reached' : 'Start Recording'}
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Recording list */}
                        <div style={{ marginBottom: '20px' }}>
                            {recordings.map((recording, index) => (
                                <div 
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        padding: '8px',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        marginBottom: '12px',
                                        border: '1px solid #dfe1e5'
                                    }}
                                >
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'space-between',
                                        marginBottom: '4px'
                                    }}>
                                        <span style={{ 
                                            color: '#666',
                                            fontSize: '14px',
                                            fontWeight: '500'
                                        }}>
                                            Sample {index + 1} ({recordingLength[recording._id] ? 
                                                formatTime(recordingLength[recording._id]) : 
                                                (recording.duration ? formatTime(recording.duration) : '')
                                            })
                                        </span>
                                        <button
                                            onClick={() => handleDeleteRecording(recording._id)}
                                            className='red-delete-button'
                                            style={{
                                                padding: '4px 8px',
                                                backgroundColor: '#dc3545',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                fontSize: '0.8em'
                                            }}
                                        >
                                            <Trash2 size={14} />
                                            Delete
                                        </button>
                                    </div>

                                    {/* Simple audio player as fallback */}
                                    <audio 
                                        controls
                                        style={{ width: '100%', height: '12px' }}
                                        controlsList="nodownload"
                                        onError={(e) => {
                                            console.error('Audio playback error:', e.target.error);
                                            console.log('Audio URL:', recording.url);
                                            // Try to fetch the audio file directly to check for CORS or other issues
                                            fetch(recording.url)
                                                .then(response => {
                                                    console.log('Audio file response:', response);
                                                    return response.blob();
                                                })
                                                .then(blob => console.log('Audio blob:', blob))
                                                .catch(error => console.error('Error fetching audio:', error));
                                        }}
                                        onLoadStart={() => console.log('Audio loading started')}
                                        onLoadedMetadata={(e) => {
                                            // Format duration when metadata is loaded
                                            console.log('Audio metadata loaded, duration:', e.target.duration);
                                            if (isFinite(e.target.duration)) {
                                                const totalSeconds = Math.floor(e.target.duration);
                                                const minutes = Math.floor(totalSeconds / 60);
                                                const seconds = totalSeconds % 60;
                                                setRecordingLength(prev => ({
                                                    ...prev,
                                                    [recording._id]: `${minutes}:${seconds.toString().padStart(2, '0')}`
                                                }));
                                            }
                                        }}
                                        onLoadedData={() => console.log('Audio data loaded')}
                                    >
                                        <source 
                                            src={recording.url} 
                                            type="audio/webm"
                                            onError={(e) => console.error('Source error:', e)}
                                        />
                                        Your browser does not support the audio element.
                                    </audio>
                                </div>
                            ))}
                        </div>

                        {/* Clone voice button */}
                        {recordings.length >= 1 && (
                            <button
                                onClick={handleVoiceCloning}
                                className="main-button"
                                disabled={isGeneratingVoice || recordings.length < 1}
                                style={{ width: '100%' }}
                            >
                                {isGeneratingVoice ? (
                                    <>
                                        <RefreshCw className="spin" size={16} style={{marginRight:'5px'}}/>
                                        Generating Voice Clone...
                                    </>
                                ) : (
                                    'Create My AI Voice'
                                )}
                            </button>
                        )}

                    </div>

                    )}

                    {/* Test cloned voice */}
                    {userVoiceId && (
                        <div>
                            <h4 style={{color: darkMode ? "white" : "#333"}}>Test Your AI Voice</h4>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={testClonedVoice}
                                    className="main-button"
                                    disabled={isTestingVoice || !userVoiceId}
                                    style={{ flex: 1 }}
                                >
                                    {isTestingVoice? (
                                        <>
                                            <RefreshCw className="spin" size={16} style={{marginRight:'5px'}}/>
                                            Generating...
                                        </>
                                    ) : (
                                        'Generate Test'
                                    )}
                                </button>
                                {testAudioUrl && (
                                    <button
                                        onClick={() => {
                                            if (audioRef.current) {
                                                if (isPlayingVoice) {
                                                    audioRef.current.pause();
                                                } else {
                                                    audioRef.current.play();
                                                }
                                                setIsPlayingVoice(!isPlayingVoice);
                                            }
                                        }}
                                        className="main-button"
                                        style={{ width: '50px' }}
                                    >
                                        {isPlayingVoice ? (
                                            <Pause size={16} />
                                        ) : (
                                            <Play size={16} />
                                        )}
                                    </button>
                                )}
                            </div>
                            <audio
                                ref={audioRef}
                                src={testAudioUrl}
                                onEnded={() => setIsPlayingVoice(false)}
                                style={{ display: 'none' }}
                            />
                        </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                        <button
                            onClick={handleCloseAttempt}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '5px',
                                border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                cursor: 'pointer'
                            }}
                        >
                            Close
                        </button>
                        <button
                            onClick={handleSave}
                            className="main-button"
                            disabled={isSaving}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: '120px',
                                gap: '8px',
                                backgroundColor: saveStatus === 'saved' ? '#4CAF50' : saveStatus === 'error' ? '#f44336' : undefined,
                                color: saveStatus === 'saved' || saveStatus === 'error' ? 'white' : undefined,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {isSaving ? (
                                <>
                                    <RefreshCw className="spin" size={16} />
                                    Saving...
                                </>
                            ) : saveStatus === 'saved' ? (
                                <>
                                    <Check size={16} />
                                    Saved
                                </>
                            ) : saveStatus === 'error' ? (
                                <>
                                    <AlertCircle size={16} />
                                    Error
                                </>
                            ) : (
                                'Save Profile'
                            )}
                        </button>
                    </div>
                    
                </div>

                {/* Save Changes Confirmation Popup */}
                {showSaveConfirmation && (
                    <div 
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 3000
                        }}
                        onClick={handleCancelClose}
                    >
                        <div 
                            style={{
                                backgroundColor: darkMode ? '#27292D' : 'white',
                                padding: '30px',
                                borderRadius: '10px',
                                width: '90%',
                                maxWidth: '400px',
                                textAlign: 'center'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{ marginBottom: '20px', color: darkMode ? '#fff' : '#333' }}>
                                Save Changes?
                            </h3>
                            <p style={{ 
                                marginBottom: '25px', 
                                color: '#666',
                                lineHeight: '1.5'
                            }}>
                                You have unsaved changes. Do you want to save them before closing?
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                <button
                                    onClick={handleCancelClose}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        border: darkMode ? '1px solid #444' : '1px solid #dfe1e5',
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                        color: darkMode ? '#fff' : '#333'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveAndClose}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        border: 'none',
                                        backgroundColor: '#28a745',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Save & Close
                                </button>
                                <button
                                    onClick={handleConfirmClose}
                                    style={{
                                        padding: '10px 20px',
                                        borderRadius: '5px',
                                        border: 'none',
                                        backgroundColor: '#dc3545',
                                        color: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Close Without Saving
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                </div>
            </div>
        );
    }

    const [isMuted, setIsMuted] = useState(() => {
        const savedMuteState = localStorage.getItem('musicMuted');
        return savedMuteState ? JSON.parse(savedMuteState) : false;
    });
    
    // Create audio element with the saved mute state
    const [audio] = useState(() => {
        const audioElement = new Audio('/Girl from Petaluma-2.mp3');
        audioElement.volume = 0.5; // Set volume to 50%
        audioElement.loop = true; // Make it loop
        
        // Apply the initial mute state
        const savedMuteState = localStorage.getItem('musicMuted');
        audioElement.muted = savedMuteState ? JSON.parse(savedMuteState) : false;
        
        return audioElement;
    });

    useEffect(() => {
        if (analyzing) {
            audio.play().catch(error => {
                console.error('Audio playback failed:', error);
            });
        } else {
            audio.pause();
            audio.currentTime = 0;
        }

        // Cleanup function
        return () => {
            audio.pause();
            audio.currentTime = 0;
        };
    }, [analyzing, audio]);

    // Effect to update localStorage when mute state changes
    useEffect(() => {
        localStorage.setItem('musicMuted', JSON.stringify(isMuted));
        audio.muted = isMuted;
    }, [isMuted, audio]);

    // Toggle mute function
    const toggleMute = () => {
        setIsMuted(prevMuted => !prevMuted);
    };



    const calculateYearProgress = () => {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0); // Début de l'année
        const diff = now - start;
        const oneDay = 1000 * 60 * 60 * 24;
        const dayOfYear = Math.floor(diff / oneDay);
        const progress = (dayOfYear / 365) * 100;
        return progress.toFixed(1); // Retourne le pourcentage avec 1 décimale
    };

    const handleDownloadVideo = async () => {
        console.log('handleDownloadVideo called');
        try {
            // Show a loading message
            setMessage('Downloading video...');
            setMessageType('info')
            console.log('Downloading video...');
            
            // Get the video element and its source
            const videoElement = document.querySelector('.result-container video');
            console.log('videoElement:', videoElement);
            
            // Get the source element inside the video
            const sourceElement = videoElement?.querySelector('source');
            const videoSrc = sourceElement?.src || videoElement?.src || generatedVideoUrl;
            
            console.log('videoSrc:', videoSrc);
            
            if (!videoSrc) {
                setMessage('Video source not found');
                setMessageType('error')
                return;
            }
            
            // Create a blob from the video source
            const response = await fetch(videoSrc);
            console.log('response:', response);
            const blob = await response.blob();
            console.log('blob:', blob);
            
            // Create a URL for the blob
            const blobUrl = URL.createObjectURL(blob);
            console.log('blobUrl:', blobUrl);
            
            // Create a download link
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `generated-video-${Date.now()}.mp4`;
            console.log('link:', link);
            
            // Trigger the download
            document.body.appendChild(link);
            console.log('document.body.appendChild(link);');
            link.click();
            console.log('link.click();');
            document.body.removeChild(link);
            console.log('document.body.removeChild(link);');
            
            // Clean up the blob URL
            URL.revokeObjectURL(blobUrl);
            console.log('URL.revokeObjectURL(blobUrl);');
            
            // Show success message and modal
            setMessage('Video downloaded successfully!');
            setMessageType('success')
            setShowMetaModal(true);
        } catch (error) {
            console.error('Error downloading video:', error);
            setMessage('Error downloading video. Please try again.');
            setMessageType('error')
        }
    };
      // Handle file upload
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setVideo(file);
        }
        console.log('file:', file);
    };
    
    // Handle model selection
    const handleModelChange = (event) => {
        setModel(event.target.value);
    };

    // Handle file drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        
        const files = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('video/') || file.type.startsWith('image/')
        );

        if (files.length > 50) {
            setBackendError('You can only upload up to 50 files at once');
            return;
        }

        handleFiles(files);
    };

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    // Process files
    const handleFiles = async (files) => {
        for (const file of files) {
            const formData = new FormData();
            formData.append('video', file);

            try {
                const response = await api.uploadVideo(formData, {
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    }
                });

                const newVideo = {
                    id: response.data.id,
                    name: file.name,
                    url: response.data.url,
                    status: 'processing',
                    tags: [],
                    description: '',
                    cuts: []
                };

                setVideos(prev => [...prev, newVideo]);

                // Clear progress after successful upload
                setUploadProgress(prev => {
                    const newProgress = { ...prev };
                    delete newProgress[file.name];
                    return newProgress;
                });

                // Auto-cuts are now generated automatically in the backend
                console.log('Video uploaded successfully, auto-cuts will be generated in the background');
            } catch (error) {
                console.error('Upload error:', error);
                setBackendError(`Failed to upload ${file.name}`);
            }
        }
    };

    // Handle tag addition
    const handleAddTag = async (videoId) => {
        if (!newTag.trim()) return;

        try {
            await api.addVideoTag(videoId, newTag.trim());
            
            // Update local state
            setVideos(prev => prev.map(video => {
                if (video.id === videoId) {
                    return {
                        ...video,
                        tags: [...video.tags, newTag.trim()]
                    };
                }
                return video;
            }));

            setNewTag('');
        } catch (error) {
            console.error('Error adding tag:', error);
            setBackendError(`Failed to add tag: ${error.response?.data?.error || error.message}`);
        }
    };

    // Handle tag removal
    const handleRemoveTag = async (videoId, tagToRemove) => {
        try {
            await api.removeVideoTag(videoId, tagToRemove);
            
            // Update local state
            setVideos(prev => prev.map(video => {
                if (video.id === videoId) {
                    return {
                        ...video,
                        tags: video.tags.filter(tag => tag !== tagToRemove)
                    };
                }
                return video;
            }));
        } catch (error) {
            console.error('Error removing tag:', error);
            setBackendError(`Failed to remove tag: ${error.response?.data?.error || error.message}`);
        }
    };

    // Handle description update
    const handleUpdateDescription = (videoId) => {
        setVideos(prev => prev.map(video => {
            if (video.id === videoId) {
                return {
                    ...video,
                    description: newDescription
                };
            }
            return video;
        }));
        setEditingVideo(null);
        setNewDescription('');
    };

    // Fetch generated videos
    // useEffect(() => {
    //     const fetchGeneratedVideos = async () => {
    //         try {
    //             const response = await api.getUserGeneratedVideos(userId);
    //             setGeneratedVideos(response.data);
    //         } catch (error) {
    //             console.error('Error fetching generated videos:', error);
    //         }
    //     };

    //     if (userId) {
    //         fetchGeneratedVideos();
    //     }
    // }, [userId]);

    return (
        <div className="workspace">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h1>Merra.ai</h1>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {userProfile.isSubscribed && (
                        <span style={{
                            backgroundColor: '#000000',
                            border: '1px solid #FFD700',
                            color: '#FFD700',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            letterSpacing: '0.5px'
                        }}>
                            PRO
                        </span>
                    )}
                    <button 
                        className="profile-button"
                        onClick={() => setShowProfileModal(true)}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: 'none',
                            cursor: 'pointer',
                            position: 'relative',
                            padding: 0,
                            backgroundColor: userInfo.picture ? 'transparent' : '#27292D',
                        }}
                    >
                        {userInfo.picture ? (
                            <img 
                                src={userInfo.picture} 
                                alt="Profile" 
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    borderRadius: '50%'
                                }}
                            />
                        ) : (
                            <i className="fas fa-user" style={{ color: 'white' }}></i>
                        )}

                        {/* Notification Dot */}
                        {!profileComplete && (
                            <span style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '12px',
                                height: '12px',
                                backgroundColor: '#ff4444',
                                borderRadius: '50%',
                                border: '2px solid white',
                            }} />
                        )}
                    </button>
                </div>
            </div>

            {/* error banner */}
            {backendError && (
                <div style={{
                    backgroundColor: '#ff44444d',
                    color: '#ff4444',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: '1px solid #ff4444',
                    maxWidth: '500px',
                    alignSelf: 'center',
                    justifySelf: 'center',
                }}>
                    <AlertCircle size={20} />
                    {backendError}
                </div>
            )}

            {/* Profile Modal */}
            {showProfileModal && <ProfileModal />}

            {/* Greeting */}
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h2>Hello {userName?.split(' ')[0]}!</h2>
                <h2 className={darkMode ? "gradient-text-dark" : "gradient-text"} style={{justifySelf:"center", fontSize:'20px'}}>
                    Feeling awesome today?
                </h2>
            </div>
        
            {/* Video Management Component */}
            <VideoManagement disableCuts={true} />


            {/* Year Progress Bar */}
            <div style={{
                padding: '20px',
                marginTop: '30px',
                marginBottom: '30px',
                borderTop: darkMode ? '1px solid #222529' : '1px solid #666',
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    // color: '#ffffff95',
                    opacity: '0.8',
                    fontSize: '14px'
                }}>
                    <span>Year {new Date().getFullYear()} progression (reminder to do the most)</span>
                    <span>{calculateYearProgress()}% complete</span>
                </div>
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: darkMode ? '#2A2B2F' : '#214B23',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <div style={{
                        width: `${calculateYearProgress()}%`,
                        height: '100%',
                        backgroundColor: '#4CAF50',
                        borderRadius: '2px',
                        transition: 'width 0.3s ease'
                    }}/>
                </div>
                {parseFloat(calculateYearProgress()) > 50 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '8px',
                        fontSize: '12px',
                        opacity: '0.7'
                    }}>
                        <span style={{ color: '#4CAF50' }}>experience</span>
                        <span style={{ color: darkMode ? '#888' : '#666' }}>opportunity</span>
                    </div>
                )}
            </div>

            {/* Feedback Button */}
            <FeedbackButton username={userName} />

        </div>
    );
};

export default Workspace;